import qs from 'qs';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { isAfter } from 'date-fns';
import * as MSAL from '@azure/msal-browser';

import { RestResponse } from '../types/response/RestResponse';
import { API_URL } from '../constants/api';
import store from '../state/store';
import { tokenRequest } from '../constants/login/loginConfig';
import { msalInstance } from '../constants/login/msalInstance';
import { Actions as AuthActions } from '../state/auth/actions';
import { AuthResponse } from '../state/auth/types';
import { history } from '../state/history';
import { FORBIDDEN, LOGIN_ROUTE } from '../constants/routes';

export interface RestConfig {
  baseURL: string;
}

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
  isRefreshing = false;
};

export class RestService {
  axios: AxiosInstance;

  defaultHeader = () => ({
    'Content-Type': 'application/json'
  });

  constructor(config: RestConfig) {
    const axiosConfig = this.createConfig(config);
    this.axios = axios.create(axiosConfig);
    this.axios.interceptors.request.use(this.transformRequest);
    this.axios.interceptors.response.use(this.responseInterceptorOnFulfilled, this.responseInterceptorOnRejected);
    this.axios.defaults.paramsSerializer = (params: any) => qs.stringify(params, { indices: false });
  }

  createConfig = (config: RestConfig): AxiosRequestConfig => ({
    baseURL: config.baseURL,
    headers: this.defaultHeader(),
    withCredentials: false
  });

  transformRequest = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const authState = store.getState().auth.authInfo;

    if (!authState?.uniqueId) {
      const token = sessionStorage.getItem('CSFR_TOKEN');

      if (token) {
        return {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`
          }
        };
      }

      return config;
    }

    const { accessToken, expiresOn } = authState;

    if (expiresOn && isAfter(expiresOn, new Date())) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`
        }
      };
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => ({
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`
        }
      }));
    }

    isRefreshing = true;

    try {
      const accounts = msalInstance.getAllAccounts();
      const request = {
        ...tokenRequest,
        account: accounts[0]
      };
      const response = await msalInstance.acquireTokenSilent(request);
      const newAccessToken = response.accessToken;

      store.dispatch(AuthActions.silentLoginSuccess(response as AuthResponse));

      processQueue(null, newAccessToken);

      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${newAccessToken}`
        }
      };
    } catch (e: any) {
      processQueue(e, null);

      if (
        e instanceof MSAL.InteractionRequiredAuthError ||
        e?.errorCode === 'invalid_grant' ||
        e?.suberror === 'bad_token' ||
        (Array.isArray(e?.errorCodes) && e.errorCodes.includes(700084))
      ) {
        await msalInstance.loginRedirect(tokenRequest);
        return new Promise(() => {});
      }

      return Promise.reject(e);
    }
  };

  transformResponse = (response: AxiosResponse): RestResponse<any> => {
    if (response.status === 401) {
      store.dispatch(AuthActions.setNotAuthenticated());
      sessionStorage.removeItem('CSFR_TOKEN');
      history.push(LOGIN_ROUTE);
    } else if (response.status === 403) {
      history.push(FORBIDDEN);
    }

    return {
      data: response.data,
      headers: response.headers,
      status: response.status,
      statusText: response.statusText,
      isSuccessful: response.status >= 200 && response.status <= 299,
      isBadRequest: response.status === 400,
      isUnauthorized: response.status === 401,
      isAccessDenied: response.status === 403
    };
  };

  responseInterceptorOnFulfilled = (response: AxiosResponse): RestResponse<any> => this.transformResponse(response);

  responseInterceptorOnRejected = (error: any): Promise<RestResponse<any>> => {
    if (error?.requiresInteraction) {
      return Promise.reject(error);
    }

    if (error?.response) {
      return Promise.reject(this.transformResponse(error.response));
    }

    return Promise.reject(error);
  };

  isGetRequest = (method: string | undefined): boolean => (method ? method.toUpperCase() === 'GET' : false);

  request = (method: Method, url: string, data?: any, headers?: any, config: any = {}) =>
    this.axios({
      method,
      url,
      headers,
      data,
      params: this.isGetRequest(method) ? data : undefined,
      ...config
    }) as unknown as RestResponse<any>;

  get = (url: string, data?: any, headers?: any) => this.request('GET', url, data, headers);

  post = (url: string, data?: any, headers?: any, config?: any) => this.request('POST', url, data, headers, config);

  put = (url: string, data?: any, headers?: any) => this.request('PUT', url, data, headers);

  delete = (url: string, data?: any, headers?: any) => this.request('DELETE', url, data, headers);
}

export default new RestService({ baseURL: API_URL });
